import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {
  formatNamesList,
  getCoParentInvitationFromStorage,
} from '@/app/main/coparent/utils';
import {useDownloadAppTrackers} from '@/app/main/onboarding/pages/DownloadApp/trackers';
import {useLoading} from '@/app/zustand/useLoading';
import {useRemoteFeatureFlags} from '@/dataSource/src/api/firebase/useRemoteFeatureFlags';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';

import {
  AUTHENTICATION_DOWNLOAD_APP_URL,
  AUTHENTICATION_PATH,
  KID_DOWNLOAD_APP_URL,
  MODAK_MAKER_MBX_POLICY_URL,
  MODAK_MAKER_REGISTER_URL,
  NOTIFY_ME_PATH,
  PARENT_DOWNLOAD_APP_URL,
  TIME_OUT_ANIMATION,
  YOUR_INFO_IS_IN_REVIEW_PATH,
} from './models/constants';

interface IDownloadImage {
  source: string;
  width: number;
  height: number;
}

interface IFeatureItem {
  icon: string;
  label: string;
}

type ViewModelContextType = {
  isMobile: boolean;
  downloadImage: IDownloadImage;
  titleLabel: string;
  featureItems: IFeatureItem[];
  isFromLogin: boolean;
  isParent: boolean;
  showBanner: boolean;
  isNewParentOnboarding: boolean;
  qrDownloadImage: IDownloadImage;
  showMoGreenLottie: boolean;
  messageBubbleText: string;
  parentSubtitle: string;
  coParentKidsNames: {formatted: string; count: number} | null;
  isOnboardingV5Enabled: boolean;
  handleOpenDownloadApp: () => void;
  handleOpenModakRegister: () => void;
  handleOpenModakMbxPolicy: () => void;
};

const ARROW_ICON = '/assets/icons/arrow-list-right.svg';

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [PROPERTIES] ----]
  const {t} = useTranslation();
  const {stopLoading} = useLoading();
  const {isMobile} = useWindowSize();
  const {isParent} = useUsersRepository();
  const location = useLocation();
  const {flagsMap} = useRemoteFeatureFlags();
  const {
    trackParentViewDownloadApp,
    trackParentSelectDownloadApp,
    trackSelectOpenModakRegister,
    trackSelectOpenMbxPolicy,
    trackViewKidRedirection,
    trackKidSelectDownloadApp,
    trackViewDownloadApp,
    trackAuthenticationSelectDownloadApp,
  } = useDownloadAppTrackers();
  //#endregion

  //#region [---- [ROUTE FLAGS] ----]
  const pathname = useMemo(
    () => location.pathname.toLowerCase(),
    [location.pathname],
  );

  const isFromLogin = useMemo(() => {
    return pathname.includes(AUTHENTICATION_PATH);
  }, [pathname]);

  const isNotifyMe = useMemo(() => {
    return pathname.includes(NOTIFY_ME_PATH);
  }, [pathname]);

  const isYourInfoIsInReview = useMemo(() => {
    return pathname.includes(YOUR_INFO_IS_IN_REVIEW_PATH);
  }, [pathname]);

  const previousScreen = useMemo(() => {
    return new URLSearchParams(location.search).get('previousScreen') ?? '';
  }, [location.search]);

  const isOnboardingV5Enabled = flagsMap?.onboardingV5Enabled ?? false;
  const isNewParentOnboarding = useMemo(() => {
    return isOnboardingV5Enabled && isParent;
  }, [isOnboardingV5Enabled, isParent]);
  //#endregion

  //#region [---- [ANIMATIONS] ----]
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!isMobile) return;

    const timer = setTimeout(() => {
      setShowBanner(true);
    }, TIME_OUT_ANIMATION);

    return () => clearTimeout(timer);
  }, [isMobile]);
  //#endregion

  //#region [---- [TRACKERS] ----]
  useEffect(() => {
    isFromLogin
      ? trackViewDownloadApp()
      : isParent
        ? trackParentViewDownloadApp(previousScreen)
        : trackViewKidRedirection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousScreen]);
  //#endregion

  //#region [---- [COPY AND CONTENT] ----]
  const showMoGreenLottie = useMemo(() => {
    return isNotifyMe || isYourInfoIsInReview;
  }, [isNotifyMe, isYourInfoIsInReview]);

  const titleLabel = useMemo(() => {
    return isFromLogin
      ? isMobile
        ? t('download_app_page.from_login.title_mobile')
        : t('download_app_page.from_login.title_desktop')
      : isYourInfoIsInReview
        ? t('download_app_page.title_mobile_your_info_is_in_review')
        : isNotifyMe
          ? t('download_app_page.title_mobile_notify_me')
          : isMobile
            ? t('download_app_page.title_mobile')
            : t('download_app_page.title_desktop');
  }, [isFromLogin, isMobile, isNotifyMe, isYourInfoIsInReview, t]);

  const parentSubtitle = useMemo(() => {
    return isYourInfoIsInReview
      ? t('download_app_page.description_mobile_your_info_is_in_review')
      : isNotifyMe
        ? t('download_app_page.description_mobile_notify_me')
        : t('download_app_page.parentSubtitle');
  }, [isNotifyMe, isYourInfoIsInReview, t]);

  const featureItems = useMemo<IFeatureItem[]>(() => {
    if (isFromLogin) {
      return [
        {
          icon: ARROW_ICON,
          label: t('download_app_page.from_login.description_1'),
        },
        {
          icon: ARROW_ICON,
          label: t('download_app_page.from_login.description_2'),
        },
        {
          icon: ARROW_ICON,
          label: t('download_app_page.from_login.description_3'),
        },
      ];
    }

    if (isParent) {
      return [
        {icon: ARROW_ICON, label: t('download_app_page.description_2')},
        {icon: ARROW_ICON, label: t('download_app_page.description_3')},
      ];
    }

    return [
      {
        icon: '/assets/icons/mobucks_black.svg',
        label: t('download_app_page.kid.description_1'),
      },
      {
        icon: '/assets/icons/credit_card_black.svg',
        label: t('download_app_page.kid.description_2'),
      },
      {
        icon: '/assets/icons/celebration_black.svg',
        label: t('download_app_page.kid.description_3'),
      },
    ];
  }, [isFromLogin, isParent, t]);

  const coParentKidsNames = useMemo(() => {
    const invitation = getCoParentInvitationFromStorage();
    if (!invitation) return null;

    const kids =
      invitation.kids?.filter(Boolean) ??
      invitation.rawParams?.kids
        ?.split(',')
        .map(kid => kid.trim())
        .filter(Boolean);

    if (!kids?.length) return null;

    return {
      formatted: formatNamesList(kids),
      count: kids.length,
    };
  }, []);

  const messageBubbleText = useMemo(() => {
    if (isYourInfoIsInReview) {
      return t('download_app_page.account_updates');
    }

    if (isNotifyMe) {
      return '';
    }

    if (coParentKidsNames) {
      return t(
        coParentKidsNames.count === 1
          ? 'download_app_page.co_parent_waiting_message_single'
          : 'download_app_page.co_parent_waiting_message',
        {kids: coParentKidsNames.formatted},
      );
    }

    return '';
  }, [coParentKidsNames, isNotifyMe, isYourInfoIsInReview, t]);
  //#endregion

  //#region [---- [IMAGES] ----]
  const downloadImage = useMemo<IDownloadImage>(() => {
    const image = {
      source: isFromLogin
        ? '/assets/images/etc/download-app-phone.webp'
        : isParent
          ? '/assets/images/etc/MoCell.webp'
          : '/assets/images/etc/kid_download_app_phone.webp',
      width: isMobile ? 280 : 230,
      height: isMobile ? 320 : 208,
    };
    return image;
  }, [isFromLogin, isMobile, isParent]);

  const qrDownloadImage = useMemo<IDownloadImage>(() => {
    const image = {
      source: isFromLogin
        ? '/assets/images/etc/authentication_download_mobile_app_qr.webp'
        : isParent
          ? '/assets/images/etc/parent_download_mobile_app_qr.webp'
          : '/assets/images/etc/kid_download_mobile_app_qr.webp',
      width: 189,
      height: 189,
    };
    return image;
  }, [isFromLogin, isParent]);
  //#endregion

  //#region [---- [WEBVIEW] ----]
  const isWebViewActive = useMemo(() => {
    return (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    );
  }, []);

  const handleCloseWebView = useCallback(() => {
    stopLoading();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({status: WEB_VIEW_MESSAGES.CLOSE_WEBVIEW}),
    );
  }, [stopLoading]);

  useEffect(() => {
    if (isWebViewActive) {
      handleCloseWebView();
    }
  }, [handleCloseWebView, isWebViewActive]);
  //#endregion

  //#region [---- [ACTIONS] ----]
  const handleOpenDownloadApp = useCallback(() => {
    isFromLogin
      ? trackAuthenticationSelectDownloadApp()
      : isParent
        ? trackParentSelectDownloadApp()
        : trackKidSelectDownloadApp();

    const urlToRedirect = isFromLogin
      ? AUTHENTICATION_DOWNLOAD_APP_URL
      : isParent
        ? PARENT_DOWNLOAD_APP_URL
        : KID_DOWNLOAD_APP_URL;
    window.open(urlToRedirect, '_blank');
  }, [
    isFromLogin,
    isParent,
    trackAuthenticationSelectDownloadApp,
    trackKidSelectDownloadApp,
    trackParentSelectDownloadApp,
  ]);

  const handleOpenModakRegister = useCallback(() => {
    trackSelectOpenModakRegister();
    window.open(MODAK_MAKER_REGISTER_URL, '_self');
  }, [trackSelectOpenModakRegister]);

  const handleOpenModakMbxPolicy = useCallback(() => {
    trackSelectOpenMbxPolicy();
    window.open(MODAK_MAKER_MBX_POLICY_URL, '_blank');
  }, [trackSelectOpenMbxPolicy]);
  //#endregion

  return (
    <ViewModelContext.Provider
      value={{
        isNewParentOnboarding,
        isOnboardingV5Enabled: flagsMap?.onboardingV5Enabled ?? false,
        isMobile,
        downloadImage,
        titleLabel,
        featureItems,
        isFromLogin,
        isParent,
        showBanner,
        qrDownloadImage,
        showMoGreenLottie,
        messageBubbleText,
        parentSubtitle,
        coParentKidsNames,
        handleOpenDownloadApp,
        handleOpenModakRegister,
        handleOpenModakMbxPolicy,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('OnboardingDownloadApp ViewModelContext is undefined');
  }
  return context;
}
